import { useCallback, useEffect, useState } from "react";
import { todayIST } from "@/lib/istDate";
import { FileDown, RefreshCcw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, API_BASE, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import PageHeader from "@/components/PageHeader";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";

/**
 * Generic visit report page — one of three views (beat-coverage /
 * missed-visits / daily-visit-summary). Kept generic so all three share this
 * page.  `kind` prop selects endpoint + column set.
 */
export default function VisitReportPage({ kind }) {
  const cfg = REPORT_CFG[kind];
  const today = todayIST();
  const from7 = todayIST();   // opens on today
  const [filters, setFilters] = useState({ date_from: from7, date_to: today, zone_id: "", sub_zone_id: "", user_id: "" });
  const [data, setData] = useState(null);
  const [zones, setZones] = useState([]);
  const [subZones, setSubZones] = useState([]);
  const [users, setUsers] = useState([]);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/zones"),
      api.get("/users", { params: { page_size: 200, role_code: "SE" } }),
    ]).then(([{ data: z }, { data: u }]) => {
      setZones(z.items || []);
      setUsers((u.items || []).filter((x) => x.role_code === "SE"));
    });
  }, []);

  // Phase 24A — cascading sub-zone
  useEffect(() => {
    if (!filters.zone_id) { setSubZones([]); return; }
    api.get("/geography/sub-zones", { params: { zone_id: filters.zone_id } })
       .then(r => setSubZones(r.data.items || [])).catch(() => setSubZones([]));
  }, [filters.zone_id]);

  const params = useCallback(() => {
    const p = { date_from: filters.date_from, date_to: filters.date_to };
    if (filters.zone_id) p.zone_id = filters.zone_id;
    if (filters.sub_zone_id) p.sub_zone_id = filters.sub_zone_id;
    if (filters.user_id) p.user_id = filters.user_id;
    return p;
  }, [filters]);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const { data: j } = await api.get(cfg.endpoint, { params: params() });
      setData(j);
    } catch (e) { toast.error(String(e)); }
    finally { setBusy(false); }
  }, [cfg.endpoint, params]);
  useEffect(() => { load(); }, [load]);

  const doExport = async (format) => {
    setExporting(true);
    try {
      const qs = new URLSearchParams({ ...params(), format });
      const url = `${API_BASE}${cfg.endpoint}/export?${qs.toString()}`;
      const res = await fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error?.message || j.detail || `HTTP ${res.status}`);
      }
      const blob = await res.blob();
      const u = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = u;
      a.download = `${cfg.filename}_${filters.date_from}_to_${filters.date_to}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(u);
      toast.success(`Downloaded ${format.toUpperCase()}`);
    } catch (e) { toast.error(String(e.message || e)); }
    finally { setExporting(false); }
  };

  return (
    <div className="space-y-4" data-testid={`${kind}-report-page`}>
      {/* This component renders at 3 different routes via `kind`
          (beat-coverage / missed-visits / daily-visit-summary) — the
          favorite must vary with it too, or pinning any of the three
          overwrites the same wrong, non-existent "/admin/visit-report"
          entry for all of them. cfg.endpoint is exactly the real
          registered route for this kind (see App.js). */}
      <PageHeader
        breadcrumbs={[{ label: "Reports", to: "/" }, { label: "Visits" }]}
        title="Visit report"
        subtitle="Per-user daily visit summary with beat coverage and productivity."
        favorite={{ key: `reports.visit.${kind}`, type: "screen", label: cfg.title, route: cfg.endpoint }}
        testid="visitreportpage-page-header"
      />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900">{cfg.title}</h1>
          <p className="text-sm text-neutral-500">{cfg.subtitle}</p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button className="tulsi-gradient text-white" disabled={exporting} data-testid={`${kind}-export-menu`}>
              {exporting ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <FileDown className="h-4 w-4 mr-2" />}
              Export
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => doExport("xlsx")} data-testid={`${kind}-export-xlsx`}>
              Branded Excel (.xlsx)
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => doExport("pdf")} data-testid={`${kind}-export-pdf`}>
              Branded PDF (.pdf)
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <Card className="border-neutral-200 rounded-2xl">
        <CardContent className="p-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><Label className="text-[11px]">From</Label>
            <Input type="date" value={filters.date_from} onChange={(e) => setFilters((s) => ({ ...s, date_from: e.target.value }))} /></div>
          <div><Label className="text-[11px]">To</Label>
            <Input type="date" value={filters.date_to} onChange={(e) => setFilters((s) => ({ ...s, date_to: e.target.value }))} /></div>
          <div>
            <Label className="text-[11px]">Zone</Label>
            <Select value={filters.zone_id || "all"}
                    onValueChange={(v) => setFilters((s) => ({ ...s, zone_id: v === "all" ? "" : v, sub_zone_id: "" }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All zones</SelectItem>
                {zones.map((z) => <SelectItem key={z.id} value={z.id}>{z.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">Sub-Zone</Label>
            <Select value={filters.sub_zone_id || "all"}
                    onValueChange={(v) => setFilters((s) => ({ ...s, sub_zone_id: v === "all" ? "" : v }))}
                    disabled={!filters.zone_id}>
              <SelectTrigger data-testid="report-subzone"><SelectValue placeholder={filters.zone_id ? "All sub-zones" : "Pick zone"} /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All sub-zones</SelectItem>
                {subZones.map((sz) => <SelectItem key={sz.id} value={sz.id}>{sz.code}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px]">SE</Label>
            <Select value={filters.user_id || "all"}
                    onValueChange={(v) => setFilters((s) => ({ ...s, user_id: v === "all" ? "" : v }))}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All SEs</SelectItem>
                {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {data?.summary && (
        <div className="flex flex-wrap gap-2" data-testid={`${kind}-summary`}>
          {Object.entries(data.summary).map(([k, v]) => (
            <div key={k} className="rounded-xl bg-[#6D2C91]/10 text-[#6D2C91] px-3 py-1.5 text-sm font-semibold">
              {k.replace(/_/g, " ")}: {v}
            </div>
          ))}
        </div>
      )}

      <Card className="border-neutral-200 rounded-2xl overflow-x-auto">
        <CardContent className="p-0">
          <table className="w-full text-sm" data-testid={`${kind}-table`}>
            <thead className="bg-neutral-50 text-[11px] uppercase text-neutral-500 tracking-widest">
              <tr>
                {cfg.columns.map((c) => (
                  <th key={c.key} className="text-left py-3 px-3">{c.label}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {busy && <tr><td colSpan={cfg.columns.length} className="py-10 text-center text-neutral-400">Loading…</td></tr>}
              {!busy && (data?.items || []).length === 0 && (
                <tr><td colSpan={cfg.columns.length} className="py-10 text-center text-neutral-400">No data</td></tr>
              )}
              {(data?.items || []).map((r, idx) => (
                <tr key={idx} className="border-t border-neutral-100">
                  {cfg.columns.map((c) => (
                    <td key={c.key} className={`py-2.5 px-3 ${c.mono ? "font-mono text-[12px]" : ""}`}>
                      {c.render ? c.render(r) : (r[c.key] ?? "—")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}

const REPORT_CFG = {
  "beat-coverage": {
    title: "Beat-wise coverage", subtitle: "Planned vs done visits per beat.",
    endpoint: "/reports/beat-coverage", filename: "beat_coverage",
    columns: [
      { key: "beat_code", label: "Beat", mono: true },
      { key: "beat_name", label: "Name" },
      { key: "area", label: "Distributor" },
      { key: "zone", label: "Zone" },
      { key: "assigned_se", label: "SE" },
      { key: "planned_visits", label: "Planned" },
      { key: "done_visits", label: "Done" },
      { key: "productive_visits", label: "Productive" },
      { key: "coverage_pct", label: "Coverage %", render: (r) => `${r.coverage_pct}%` },
      { key: "productive_pct", label: "Prod %", render: (r) => `${r.productive_pct}%` },
    ],
  },
  "missed-visits": {
    title: "Missed visits", subtitle: "Outlets planned but not visited.",
    endpoint: "/reports/missed-visits", filename: "missed_visits",
    columns: [
      { key: "date", label: "Date", mono: true },
      { key: "se_name", label: "SE" },
      { key: "beat_code", label: "Beat", mono: true },
      { key: "retailer_code", label: "Outlet Code", mono: true },
      { key: "retailer_name", label: "Outlet" },
      { key: "status", label: "Status" },
      { key: "skip_reason", label: "Skip reason" },
    ],
  },
  "daily-visit-summary": {
    title: "Daily visit summary", subtitle: "One row per SE per day.",
    endpoint: "/reports/daily-visit-summary", filename: "daily_visits",
    columns: [
      { key: "date", label: "Date", mono: true },
      { key: "se_name", label: "SE" },
      { key: "zone", label: "Zone" },
      { key: "planned", label: "Planned" },
      { key: "done", label: "Done" },
      { key: "productive", label: "Prod" },
      { key: "non_productive", label: "Non-Prod" },
      { key: "skipped", label: "Skip" },
      { key: "coverage_pct", label: "Coverage %", render: (r) => `${r.coverage_pct}%` },
      { key: "productive_pct", label: "Prod %", render: (r) => `${r.productive_pct}%` },
    ],
  },
};
